import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import UserProfile from "users/information/profile-information/user-profile";
import UserProfileSchema from "users/information/profile-information/user-profile-schema";
import { z } from "zod";

export type UserNetworkConnectionJSON = z.infer<typeof UserNetworkConnectionSchema.UserNetworkConnectionJSONZ>;
export type UserNetworkConnectionParams = z.infer<typeof UserNetworkConnectionSchema.UserNetworkConnectionZ>;

export default class UserNetworkConnectionSchema {
  public static readonly UserNetworkConnectionJSONZ = z.object({
    id: z.uuid(),
    createdDate: z.iso.datetime({ offset: true }),
    userA: UserProfileSchema.UserProfileJSONZ,
    userB: UserProfileSchema.UserProfileJSONZ,
  });

  public static readonly UserNetworkConnectionZ = z.object({
    id: z.instanceof(Guid),
    createdDate: z.instanceof(AHDate),
    userA: z.instanceof(UserProfile),
    userB: z.instanceof(UserProfile),
  });

  public static validate(values: unknown): UserNetworkConnectionParams {
    return parseOrThrow(UserNetworkConnectionSchema.UserNetworkConnectionZ, values, {
      logError: true,
      schemaName: "UserNetworkConnectionSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): UserNetworkConnectionJSON {
    return parseOrThrow(UserNetworkConnectionSchema.UserNetworkConnectionJSONZ, json, {
      logError: true,
      schemaName: "UserNetworkConnectionSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is UserNetworkConnectionParams {
    try {
      UserNetworkConnectionSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is UserNetworkConnectionJSON {
    try {
      UserNetworkConnectionSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: UserNetworkConnectionJSON): UserNetworkConnectionParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(UserNetworkConnectionSchema.UserNetworkConnectionJSONZ, json, {
      logError: true,
      schemaName: "UserNetworkConnectionSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return UserNetworkConnectionSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: UserNetworkConnectionParams): UserNetworkConnectionJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = UserNetworkConnectionSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return UserNetworkConnectionSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    UserNetworkConnectionSchema.UserNetworkConnectionJSONZ,
    UserNetworkConnectionSchema.UserNetworkConnectionZ,
    {
      decode: (data) => ({
        id: new Guid(GuidSchema.deserialize(data.id)),
        createdDate: new AHDate(DateSchema.deserialize(data.createdDate)),
        userA: new UserProfile(UserProfileSchema.deserialize(data.userA)),
        userB: new UserProfile(UserProfileSchema.deserialize(data.userB)),
      }),
      encode: (entity) => ({
        id: entity.id.toJSON(),
        createdDate: entity.createdDate.toJSON(),
        userA: entity.userA.toJSON(),
        userB: entity.userB.toJSON(),
      }),
    }
  );
}
